import { state, snapshot, applyData } from "./state";
import { statusBox, conflictBar } from "./dom";
import { encodeState, decodeState } from "../shared/codec";
import { mergePlans } from "../shared/merge";
import { recordPlan } from "./recents";
import type { PlanData } from "../shared/types";

const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// The last-synced snapshot (merge base for coop auto-merge). Captured at load + after each
// successful save, so a 409 can 3-way merge base/local(remote) without a network round-trip.
let baseSnapshot: PlanData | null = null;
export function captureBase(): void { baseSnapshot = structuredClone(snapshot()); }

// re-render after an auto-merge applies remote changes (injected from main to avoid an import cycle)
let rerender: () => void = () => {};
export function setRerender(fn: () => void): void { rerender = fn; }
let mergeRetries = 0; // livelock guard when another member keeps saving mid-merge

// "client": plan compressed into the URL hash on /p; "server": stored under /plan/{guid}
export function detectMode(): void {
  const m = location.pathname.match(/^\/plan\/([^/]+)\/?$/);
  if (m && GUID_RE.test(m[1])) { state.mode = "server"; state.guid = m[1].toLowerCase(); }
  else { state.mode = "client"; state.guid = null; }
}

let saveTimer: ReturnType<typeof setTimeout> | null = null;
let saving = false;
let pendingSave = false;

export function save(): void {
  state.changes++; // count this edit
  state.lastModified = new Date().toISOString();
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(() => { state.mode === "server" ? saveToServer() : saveToHash(); }, 250);
}

async function saveToHash(): Promise<void> {
  try {
    history.replaceState(null, "", "#" + (await encodeState(snapshot())));
  } catch (e) { console.error("save failed", e); }
}

async function saveToServer(): Promise<void> {
  if (saving) { pendingSave = true; return; } // collapse rapid edits into the next PUT
  saving = true;
  setStatus("saving");
  try {
    const res = await fetch("/api/projects/" + state.guid, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ baseVersion: state.serverVersion, data: snapshot() }),
    });
    if (res.status === 409) { tryAutoMerge(await res.json()); return; }
    if (!res.ok) throw new Error("HTTP " + res.status);
    const body = await res.json();
    state.serverVersion = body.version;
    captureBase(); // this snapshot is now the synced base
    mergeRetries = 0;
    if (state.guid) recordPlan(state.guid, state.projectName); // keep the recents name fresh
    setStatus("idle");
  } catch (e) {
    console.error("server save failed", e);
    setStatus("error");
  } finally {
    saving = false;
    // retry the coalesced change — unless we're blocked on an unresolved conflict
    if (pendingSave && conflictBar.hidden) { pendingSave = false; saveToServer(); }
  }
}

export async function load(): Promise<boolean> {
  return state.mode === "server" ? loadFromServer() : loadFromHash();
}

async function loadFromHash(): Promise<boolean> {
  const hash = location.hash.slice(1);
  if (!hash) return false;
  try { applyData(await decodeState(hash)); return true; }
  catch (e) { console.error("load failed", e); return false; }
}

async function loadFromServer(): Promise<boolean> {
  try {
    const res = await fetch("/api/projects/" + state.guid);
    if (res.status === 404) return false; // unknown guid → start fresh; first save creates it
    if (!res.ok) throw new Error("HTTP " + res.status);
    const body = await res.json();
    state.serverVersion = body.version || 0;
    applyData(body.data || {});
    captureBase();
    if (state.guid) recordPlan(state.guid, state.projectName); // remember this opened plan
    return true;
  } catch (e) { console.error("server load failed", e); return false; }
}

// coop auto-merge: on a 409, 3-way merge our edits with the server's concurrent edits. If
// nothing truly conflicts, apply the merge, re-base to the server's version, re-render, and
// re-save silently. If it does conflict, fall back to the reload/overwrite banner.
function tryAutoMerge(body: any): void {
  const remote: PlanData = body.data || {};
  if (baseSnapshot && mergeRetries < 5) {
    const { data: merged, conflicts } = mergePlans(baseSnapshot, snapshot(), remote);
    if (conflicts.length === 0) {
      mergeRetries++;
      applyData(merged);
      state.serverVersion = Number(body.version) || state.serverVersion; // adopt remote's version as our base
      baseSnapshot = structuredClone(merged);
      rerender();
      setStatus("idle");
      flashStatus();
      save(); // push the merged result (baseVersion now matches remote → writes the next version)
      return;
    }
  }
  showConflict(body); // unresolvable (or no base / retry cap) → manual reload/overwrite
}

// re-base the optimistic version to the server's current latest (after an import)
export async function relinkServerVersion(): Promise<void> {
  try {
    const res = await fetch("/api/projects/" + state.guid);
    if (res.ok) { const body = await res.json(); state.serverVersion = body.version || state.serverVersion; }
  } catch (e) { console.error("relink failed", e); }
}

/* ---------- status pill + version-conflict banner ---------- */
export function setStatus(s?: string): void {
  if (!statusBox) return;
  statusBox.className = "status-box " + state.mode;
  if (state.mode === "client") { statusBox.innerHTML = '<span class="dot"></span>Local · in URL'; return; }
  let label;
  if (s === "saving") { statusBox.classList.add("saving"); label = "Saving…"; }
  else if (s === "error") label = "⚠ save failed";
  else if (s === "conflict") label = "⚠ conflict";
  else label = "Server · v" + state.serverVersion;
  statusBox.innerHTML = '<span class="dot"></span>' + label;
}

export function flashStatus(): void {
  if (!statusBox) return;
  statusBox.classList.remove("bump"); void statusBox.offsetWidth; statusBox.classList.add("bump");
}

export function showConflict(body: any): void {
  (document.getElementById("conflictMsg") as HTMLElement).textContent =
    "⚠ Two saves clashed — the server is at v" + body.version + " but you were editing v" + state.serverVersion +
    ". Another save was made elsewhere.";
  conflictBar.dataset.serverVersion = body.version;
  conflictBar.dataset.serverData = JSON.stringify(body.data || {});
  conflictBar.hidden = false;
  setStatus("conflict");
}

export function hideConflict(): void { conflictBar.hidden = true; }

export async function switchToServer(): Promise<void> {
  try {
    const res = await fetch("/api/projects", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ data: snapshot() }),
    });
    if (!res.ok) throw new Error("HTTP " + res.status);
    const body = await res.json();
    location.href = "/plan/" + body.guid; // reload into server mode
  } catch (e: any) { alert("Could not create a server project: " + e.message); }
}

export async function switchToClient(): Promise<void> {
  const enc = await encodeState(snapshot());
  location.href = location.origin + "/p#" + enc; // reload into local mode with the plan in the URL
}
